/**
 * `usePatternStore` — сессионный стор для pattern workflow.
 *
 * Управляет срезами (`Slice`) и паттернами (`Pattern`) в рамках одной
 * загрузки MIDI. Стор не персистится (Q6): при перезагрузке/новом файле
 * всё сбрасывается.
 *
 * API:
 * - `state.slices`, `addSlice(range)`, `removeSlice(id)`, `setSlicePattern(...)`.
 * - `state.patterns`, `upsertPattern(pattern)`, `deletePattern(id)`.
 * - `reset()` — полный сброс (новый файл).
 *
 * **Reducer — единственный источник id.** Мутации вроде `addSlice`
 * возвращают `void`; вызывающий код читает свежий срез из `state.slices`
 * (последний элемент). Это гарантирует, что id в `state` и в
 * возвращаемом объекте всегда совпадают (раньше `genSliceId()` вызывался
 * дважды — до диспатча и внутри reducer'а — и они расходились).
 */

import { useCallback, useReducer } from "react";
import {
  type Pattern,
  type PatternId,
  type Slice,
  type SliceRange,
} from "@midi-fantom-utils/midi-util";

export type PatternStoreState = {
  slices: ReadonlyArray<Slice>;
  patterns: ReadonlyMap<PatternId, Pattern>;
};

type Action =
  | { type: "addSlice"; range: SliceRange; id: string }
  | { type: "removeSlice"; id: string }
  | { type: "setSlicePattern"; sliceId: string; patternId: PatternId | undefined }
  | { type: "upsertPattern"; pattern: Pattern }
  | { type: "deletePattern"; id: PatternId }
  | { type: "reset" };

const initialState: PatternStoreState = {
  slices: [],
  patterns: new Map(),
};

const reducer = (state: PatternStoreState, action: Action): PatternStoreState => {
  switch (action.type) {
    case "addSlice": {
      const slice: Slice = { id: action.id, range: action.range };
      return { ...state, slices: [...state.slices, slice] };
    }
    case "removeSlice": {
      // Только удаляем срез. Если у него был `patternId`, оставляем
      // паттерн в `state.patterns` — пользовательский PatternScreen
      // остаётся источником истины для слотов. Удаление паттерна —
      // отдельная операция через `deletePattern(id)`.
      return {
        ...state,
        slices: state.slices.filter((s) => s.id !== action.id),
      };
    }
    case "setSlicePattern": {
      return {
        ...state,
        slices: state.slices.map((s) =>
          s.id === action.sliceId
            ? { ...s, patternId: action.patternId }
            : s
        ),
      };
    }
    case "upsertPattern": {
      const next = new Map(state.patterns);
      next.set(action.pattern.id, action.pattern);
      return { ...state, patterns: next };
    }
    case "deletePattern": {
      const next = new Map(state.patterns);
      next.delete(action.id);
      // Снимаем ссылку с любого среза, который указывал на удалённый
      // паттерн.
      const slices = state.slices.map((s) =>
        s.patternId === action.id ? { ...s, patternId: undefined } : s
      );
      return { slices, patterns: next };
    }
    case "reset":
      return initialState;
    default:
      return state;
  }
};

let nextSliceId = 1;
const genSliceId = (): string => `s${nextSliceId++}`;

export type PatternStoreApi = {
  state: PatternStoreState;
  reset: () => void;
  addSlice: (range: SliceRange) => void;
  removeSlice: (id: string) => void;
  setSlicePattern: (sliceId: string, patternId: PatternId | undefined) => void;
  upsertPattern: (pattern: Pattern) => void;
  deletePattern: (id: PatternId) => void;
};

export const usePatternStore = (): PatternStoreApi => {
  const [state, dispatch] = useReducer(reducer, initialState);

  const reset = useCallback(() => dispatch({ type: "reset" }), []);
  const addSlice = useCallback((range: SliceRange): void => {
    dispatch({ type: "addSlice", range, id: genSliceId() });
  }, []);
  const removeSlice = useCallback((id: string) => {
    dispatch({ type: "removeSlice", id });
  }, []);
  const setSlicePattern = useCallback(
    (sliceId: string, patternId: PatternId | undefined) => {
      dispatch({ type: "setSlicePattern", sliceId, patternId });
    },
    []
  );
  const upsertPattern = useCallback((pattern: Pattern) => {
    dispatch({ type: "upsertPattern", pattern });
  }, []);
  const deletePattern = useCallback((id: PatternId) => {
    dispatch({ type: "deletePattern", id });
  }, []);

  return {
    state,
    reset,
    addSlice,
    removeSlice,
    setSlicePattern,
    upsertPattern,
    deletePattern,
  };
};
